import React, { useEffect, useRef, useState } from "react";
import ExtraSmallCard from "../../../Shared-ui/SharedComponents/Cards/ExtraSmallCard";
import {
  Box,
  Button,
  ButtonProps,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Pagination,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import variables from "../../../assets/styles/variables.module.scss";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch } from "react-redux";
import { GroupOld } from "../../../Shared-ui/Types/common";
import { group } from "console";
import { CREATE_GROUP, GET_GROUP } from "../../../Services/Endpoint";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import MetaInstance from "../../../Services/AxiosInstance/MetaInstance";
import {
  ShowError,
  ShowSuccess,
} from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import Confirm from "../../../Shared-ui/Types/Confirm";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";
import { DefaultGroupList } from "../../../Constant/Setting";
import { isOnlyAlphabetsNumbers } from "../../../Shared-ui/utils/Common/Common";
import Breadcrumb from "../../../Shared-ui/SharedComponents/Breadcrumb";

const ColorButton = styled(Button)<ButtonProps>(({ theme }) => ({
  // borderRadius: "8px",
  color: variables.whiteColor,
  backgroundColor: variables.primaryButton,
  "&:hover": {
    backgroundColor: variables.primaryColor,
  },
}));

const Groups = () => {
  const groupInitial: GroupOld = {
    description: "",
    name: "",
  };
  const theme = useTheme();
  const dispatch = useDispatch();
  const [groups, setGroups] = useState<GroupOld[]>([]);
  const [group, setGroup] = useState(groupInitial);
  const [groupCount, setGroupCount] = useState(0);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [pageSize, setPageSize] = useState<number>(12);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState<number>(1);
  const prevPage = useRef<number>(page);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [defaultGroups, setDefaultGroup] = useState<string[]>([]);
  const [confirmPopupData, setConfirmPopupData] = useState<Confirm>({
    show: false,
  });

  const [formError, setFormError] = useState({
    name: { error: "", value: false },
    description: { error: "", value: false },
  });

  const validateForm = () => {
    let isValid = true;

    if (!group?.name?.trim()) {
      setFormError((prev) => ({
        ...prev,
        name: { error: "Name is required", value: true },
      }));
      isValid = false;
    } else if (!isOnlyAlphabetsNumbers(group?.name)) {
      setFormError((prev) => ({
        ...prev,
        name: { error: "Enter A-z and 0-9 Characters", value: true },
      }));
      return false;
    } else {
      setFormError((prev) => ({
        ...prev,
        name: { error: "", value: false },
      }));
    }

    if (!group?.description?.trim()) {
      setFormError((prev) => ({
        ...prev,
        description: { error: "Description is required", value: true },
      }));
      isValid = false;
    } else {
      setFormError((prev) => ({
        ...prev,
        description: { error: "", value: false },
      }));
    }

    return isValid;
  };

  useEffect(() => {
    getGroups();
    setDefaultGroup([...DefaultGroupList]);
  }, []);

  useEffect(() => {
    setPage(1);
    if (searchText === "") {
      getGroups();
    }
  }, [searchText]);

  useEffect(() => {
    if (prevPage.current !== page) {
      getGroups();
      prevPage.current = page;
    }
  }, [page]);

  const getGroups = async () => {
    try {
      dispatch(StartLoading());
      const data = await MetaInstance.get(
        `${GET_GROUP}?page=${page}&limit=${pageSize}&search=${searchText}`
      ).then((res) => res?.data?.detail);
      setGroups(data.response);
      setGroupCount(data.count);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail?.response));
    } finally {
      dispatch(StopLoading());
    }
  };
  const handleCreate = async () => {
    if (!validateForm()) return;
    try {
      dispatch(StartLoading());
      let data;
      data = await MetaInstance.post(`${CREATE_GROUP}`, group).then(
        (res) => res?.data?.detail
      );
      dispatch(ShowSuccess(data.response));
      handleClose();
      getGroups();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail?.response));
    } finally {
      dispatch(StopLoading());
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setGroup((preVal) => ({ ...preVal, [name]: value }));
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setEditing(false);
    setGroup(groupInitial);
    setOpen(false);
  };

  const deleteGroup = async (row: GroupOld) => {
    try {
      setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
      dispatch(StartLoading());
      const data = await MetaInstance.delete(
        `${CREATE_GROUP}?name=${row.name}`
      ).then((res) => res?.data?.detail);
      dispatch(ShowSuccess(data.response));
      handleClose();
      getGroups();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail?.response));
    } finally {
      dispatch(StopLoading());
    }
  };

  const confirmDelete = (row: GroupOld) => {
    setConfirmPopupData({
      show: true,
      setConfirmPopupData: setConfirmPopupData,
      title: "Confirm Delete",
      info: "Are you sure want to delete your data? This action cannot be undone.",
      varient: "delete",
      functionName: deleteGroup,
      functionParam: row,
    });
  };
  const EditGroup = (row: GroupOld) => {
    setGroup(row);
    setOpen(true);
    setEditing(true);
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            //pb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { md: "flex-end", sm: "flex-start" },
              gap: { md: "10px", sm: "0" },
              flexDirection: { md: "row", sm: "column", xs: "column" },
            }}
          >
            <Breadcrumb data={[{ name: "Groups", path: "" }]} />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: "250px",
              }}
            >
              <OutlinedInput
                fullWidth
                // variant="outlined"
                size="small"
                placeholder="Search here"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => getGroups()}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
            <ColorButton
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              size={isSmallScreen ? "small" : "large"}
              sx={{
                textTransform: "capitalize",
                fontWeight: "400",
                whiteSpace: "nowrap",
                borderRadius: { sm: "8px", xs: "4px" },
                height: "40px",
              }}
              onClick={() => handleClickOpen()}
            >
              Create Group
            </ColorButton>
            <Dialog
              fullWidth={true}
              open={open}
              onClose={handleClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {editing ? "Update Group" : "Create Group"}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <DialogContent>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    //width: { md: "500px", sm: "380px", xs: "auto" },
                    pt: "10px",
                  }}
                >
                  <Box>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      error={formError.name.value}
                    >
                      <InputLabel
                        htmlFor="name"
                        shrink
                        sx={{ background: "#fff", px: 0.5 }}
                      >
                        Group Name *
                      </InputLabel>
                      <TextField
                        id="name"
                        name="name"
                        placeholder="Enter group name"
                        value={group.name}
                        sx={{ background: "white" }}
                        onChange={handleChange}
                        error={formError.name.value}
                        helperText={formError.name.error}
                      />
                    </FormControl>
                  </Box>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    //error={loginErr.email.value}
                  >
                    <InputLabel
                      htmlFor="description"
                      shrink
                      sx={{ background: "#fff", px: 0.5 }}
                    >
                      Description *
                    </InputLabel>
                    <TextField
                      required
                      id="description"
                      name="description"
                      placeholder="Enter description "
                      multiline
                      rows={3}
                      value={group.description}
                      sx={{ background: "white" }}
                      onChange={handleChange}
                      error={formError.description.value}
                      helperText={formError.description.error}
                    />
                  </FormControl>
                </Box>
              </DialogContent>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  padding: "24px",
                  pt: "0",
                }}
              >
                <Button
                  variant="outlined"
                  disableElevation
                  size="large"
                  sx={{
                    textTransform: "capitalize",
                  }}
                  onClick={handleClose}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  disableElevation
                  size="large"
                  autoFocus
                  sx={{
                    textTransform: "capitalize",
                  }}
                  onClick={handleCreate}
                >
                  Create
                </Button>
              </Box>
            </Dialog>
          </Box>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xl: "1fr 1fr 1fr",
              lg: "1fr 1fr 1fr",
              md: "1fr 1fr",
            },
            gap: 3,
            gridAutoRows: "1fr",
          }}
        >
          {groups.map((group, index) => {
            //if(group.name && !defaultGroups.includes(group.name)){
            if (group.name) {
              return (
                <Box key={index}>
                  <ExtraSmallCard
                    data={group}
                    deleteFun={confirmDelete}
                    editFun={EditGroup}
                    hideEdit={true}
                    key={index}
                  />
                </Box>
              );
            }
          })}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          {groupCount > pageSize && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
              }}
            >
              <Pagination
                count={Math.ceil(groupCount / pageSize)}
                page={page}
                onChange={handlePageChange}
                variant="outlined"
                shape="rounded"
              />
            </Box>
          )}
        </Box>
      </Container>
      <ConfirmPopup details={confirmPopupData} />
    </>
  );
};

export default Groups;
